import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { fetchCurrentUser } from '../api/auth';
import { clearAuthToken, getAuthToken } from '../utils/auth';

const ProtectedRoute: FC<PropsWithChildren> = ({ children }) => {
  const [status, setStatus] = useState<'pending' | 'authorized' | 'unauthorized'>(
    'pending',
  );
  const [meRole, setMeRole] = useState<string>('');
  const location = useLocation();

  useEffect(() => {
    const token = getAuthToken();

    if (!token) {
      setStatus('unauthorized');
      return;
    }

    let isMounted = true;

    fetchCurrentUser()
      .then((me) => {
        if (isMounted) {
          const role = me.role ?? me.user.typeKey ?? null;
          setMeRole((role ?? '') as string);
          const allowed = role === 'ADMIN' || role === 'DIRECTOR' || role === 'ADVISOR' || role === 'STORE_MANAGER';
          if (allowed) {
            setStatus('authorized');
          } else {
            setStatus('unauthorized');
          }
        }
      })
      .catch(() => {
        clearAuthToken();
        if (isMounted) {
          setStatus('unauthorized');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (status === 'pending') {
    return <div className="p-6">Loading...</div>;
  }

  if (status === 'unauthorized') {
    return <Navigate to={'/login'} replace />;
  }

  const upper = (meRole || '').toUpperCase();
  if (upper === 'ADVISOR') {
    const path = location.pathname;

    const fullAccessPatterns = [
      /^\/dashboard(\/|$)/i,
      /^\/crm(\/|$)/i,
      /^\/order-management(\/|$)/i,
      /^\/appointments(\/|$)/i,
      /^\/scheduler(\/|$)/i,
    ];

    const readOnlyPatterns = [
      /^\/inventory\/(masters|variants|service-variants)(?:\/[A-Za-z0-9-]+)?(?:\/[A-Za-z-]+)?$/i,
    ];

    const hasFullAccess = fullAccessPatterns.some((re) => re.test(path));
    const hasReadOnlyAccess = readOnlyPatterns.some((re) => re.test(path));

    if (hasReadOnlyAccess) {
      const isAddOrEdit = /\/(add|edit)(?:$|\/)/i.test(path);
      if (isAddOrEdit) {
        return <Navigate to={'/dashboard'} replace />;
      }
    }

    if (!hasFullAccess && !hasReadOnlyAccess) {
      return <Navigate to={'/dashboard'} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
